import { useCallback, useEffect, useRef, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { Alert, Box, Button, Page } from '@zillow/constellation-10';
import { IconPlusFilled } from '@zillow/constellation-icons';

import { logError } from '@zg-rentals/log-error';
import { ModalTenantSelect } from '@zg-rentals/modal-tenant';
import { useQueryClient } from '@zg-rentals/react-query5';

import { PortfolioFiltersProvider } from '../../contexts/PortfolioFiltersContext';
import { useGetPropertyTableList } from '../../hooks/queries/propertyTable';
import { useExpandedGroups } from '../../hooks/useExpandedGroups';
import { usePropertiesPageUserOnboarding } from '../../hooks/usePropertiesPageUserOnboarding';
import { useStreetEasyUserLink } from '../../hooks/useStreetEasyUserLink';
import accessUtils from '../../utils/accessUtils';
import {
  defaultFlexiblePropertiesFilters,
  initializeFlexiblePropertiesFiltersFromRouter,
  initializeViewModeFromRouter,
  updateFlexibleRouterWithFilters,
} from '../../utils/flexiblePropertiesPageUtils';
import {
  isRedirectToPayments,
  removeQueryParams,
  renderInitialDisplayModal,
  transformFiltersToApiParams,
} from '../../utils/propertiesPageUtils';
import { replaceRouterQueryParams } from '../../utils/routerQueryUtils';

import { FT_FLEXIBLE_PROPERTIES_PAGE_CONFIG } from '../../constants/FeatureTourConstants';
import {
  DEFAULT_FLEXIBLE_PROPERTIES_VIEW_MODE,
  FLEXIBLE_PROPERTIES_EXPANDED_GROUPS_STORAGE_KEY,
  FLEXIBLE_PROPERTIES_VIEW_MODE,
  type FlexiblePropertiesViewMode,
} from '../../constants/FlexiblePropertiesConstants';
import PropertiesConstants from '../../constants/PropertiesConstants';
import QueryConstants from '../../constants/QueryConstants';
import { EXTERNAL_ROUTES, getPaymentsDashboardRoute, ROUTES } from '../../constants/urls';

import { useAuth } from '../../components/AuthProvider';
import { FeatureTour } from '../../components/FeatureTour';
import AddPropertyFlowModal from '../../components/modals/AddPropertyFlowModal';
import PaymentsAggregateViewModal from '../../components/modals/PaymentsAggregateViewModal';
import UserOnboarding from '../../components/modals/UserOnboarding';
import EmailVerification from '../../components/properties/EmailVerification';
import NotificationBanner from '../../components/properties/NotificationBanner';
import { PropertiesPageNotifications } from '../../components/properties/PropertiesPageNotifications';
import { FlexiblePropertyCardList } from './components/FlexiblePropertyCardList';
import { FlexiblePropertyEmptyState } from './components/FlexiblePropertyEmptyState';
import { FlexiblePropertyNoResultsEmptyState } from './components/FlexiblePropertyNoResultsEmptyState';
import PropertiesTable from './components/PropertiesTable';
import { Toolbar } from './components/Toolbar';
import UnitsListTable from './components/UnitsListTable';

import { emitViewPropertiesPageEvent } from '../../analytics/events/propertiesPage';
import { client } from '../../http/zgApiClient';
import AuthCheckWrapper from '../AuthCheckWrapper';
import { PageHeading } from '../PropertiesPage/PageHeading';
import { PropertiesPageSkeleton } from '../PropertiesPage/PropertiesPageSkeleton';

import type { FlexiblePropertiesFilterState } from '../../utils/flexiblePropertiesPageUtils';

const MAX_SCROLL_ATTEMPTS = 5;

/*
 * TODO: Parity gaps vs PropertiesPage:
 *
 * 1. Analytics - data-eventid on Page.Root and add-property clickstream events.
 */
const FlexiblePropertiesPage = () => {
  const router = useRouter();

  const { user, isAuthorized } = useAuth();
  const { isLinkingStreetEasyUser } = useStreetEasyUserLink();

  const isCheckInitialDisplayModal = useRef(true);
  const hasInitializedFromQuery = useRef(false);

  const queryClient = useQueryClient();

  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  const [tenantModalListingAlias, setTenantModalListingAlias] = useState<string | null>(null);
  const [showPaymentsAggregateViewModal, setShowPaymentsAggregateViewModal] = useState(false);
  const [filters, setFilters] = useState<FlexiblePropertiesFilterState>(defaultFlexiblePropertiesFilters);
  const [viewMode, setViewMode] = useState<FlexiblePropertiesViewMode>(DEFAULT_FLEXIBLE_PROPERTIES_VIEW_MODE);
  const [pendingScrollGroupId, setPendingScrollGroupId] = useState<string | null>(null);

  // Owned here rather than by the card list so an `expandedGroup` link can open a card.
  const expansion = useExpandedGroups(FLEXIBLE_PROPERTIES_EXPANDED_GROUPS_STORAGE_KEY);
  const { expandGroup } = expansion;

  const cardNodesRef = useRef(new Map<string, HTMLElement>());
  const scrollAttemptsRef = useRef(0);

  const registerCard = useCallback((groupId: string, node: HTMLElement | null) => {
    if (node) {
      cardNodesRef.current.set(groupId, node);
    } else {
      cardNodesRef.current.delete(groupId);
    }
  }, []);

  useEffect(() => {
    if (!router.isReady || hasInitializedFromQuery.current) {
      return;
    }

    hasInitializedFromQuery.current = true;

    setFilters(initializeFlexiblePropertiesFiltersFromRouter(router.query));
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [router.isReady]);

  // Synced on every query change rather than once: the multiplex row links to this same page, so
  // neither that navigation nor the Back that undoes it remounts the component.
  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    const expandedGroup = router.query[QueryConstants.EXPANDED_GROUP];

    if (typeof expandedGroup === 'string' && expandedGroup) {
      expandGroup(expandedGroup);
      scrollAttemptsRef.current = 0;
      setPendingScrollGroupId(expandedGroup);
      setViewMode(FLEXIBLE_PROPERTIES_VIEW_MODE.BOTH);
      // Dropping both params leaves the canonical `/properties`, which already means the Both view.
      replaceRouterQueryParams(router, ROUTES.PROPERTIES, {
        [QueryConstants.EXPANDED_GROUP]: undefined,
        [QueryConstants.VIEW_MODE]: undefined,
      });

      return;
    }

    setViewMode(initializeViewModeFromRouter(router.query));
    // oxlint-disable react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [
    router.isReady,
    router.query[QueryConstants.VIEW_MODE],
    router.query[QueryConstants.EXPANDED_GROUP],
    expandGroup,
  ]);
  // oxlint-enable react-hooks/exhaustive-deps

  const skeleton = <PropertiesPageSkeleton />;

  const {
    data: propertiesData,
    error: propertiesError,
    isError,
    isPending,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetPropertyTableList(
    transformFiltersToApiParams({
      subNavFilterType: filters.includeArchived
        ? PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED
        : PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
      searchText: filters.searchText,
    }),
    {
      enabled: isAuthorized && Boolean(user?.restrictions?.isAllowed),
    },
  );

  const canAccessFeatureTour = accessUtils.canAccessFeatureTour();

  const { listingCount, listingGroups } = propertiesData;

  const { total: numTotalListings = 0, archived: numArchivedListings = 0 } = listingCount ?? {};

  const {
    canAccessUserOnboardingNewUser,
    setShowingUserOnboardingExisitngUser,
    canAccessUserOnboardingExistingUser,
    onboardingConfig,
  } = usePropertiesPageUserOnboarding({ numTotalListings });

  const isContentLoading = isPending || isFetching || isFetchingNextPage;
  // The very first fetch, before any data has resolved. Gated on `isFetching` so a disabled
  // query (e.g. restricted user) doesn't get stuck showing skeletons forever.
  const hasAnyListings = numTotalListings > 0 || numArchivedListings > 0;
  // A StreetEasy user with nothing yet is about to receive listings, so hold the skeleton instead of the empty state.
  const isAwaitingStreetEasyListings = isLinkingStreetEasyUser && !hasAnyListings;
  const isInitialLoading = (isPending && isFetching) || isAwaitingStreetEasyListings;
  const hasActiveFilters = Boolean(filters.searchText) || filters.includeArchived;
  // listingCount is the unfiltered portfolio summary, so it stays accurate while listingGroups is a stale
  // placeholder mid-refetch; deriving the unfiltered case from it avoids claiming "all archived" on an
  // account that still has active listings. Gating on isFetching would flash the table back in on every refetch.
  const isEntirelyArchived = numTotalListings === 0 && numArchivedListings > 0;
  const hasNoFilteredResults =
    hasAnyListings &&
    (listingGroups?.length ?? 0) === 0 &&
    !isInitialLoading &&
    (hasActiveFilters || isEntirelyArchived);

  // Re-runs as the page renders, since on a cold deep link the card isn't mounted yet.
  useEffect(() => {
    if (!pendingScrollGroupId || viewMode !== FLEXIBLE_PROPERTIES_VIEW_MODE.BOTH) {
      return;
    }

    const card = cardNodesRef.current.get(pendingScrollGroupId);

    if (card) {
      card.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setPendingScrollGroupId(null);

      return;
    }

    // Bounded so a group that never renders — a later results page, or filtered out — stops here.
    scrollAttemptsRef.current += 1;

    if (scrollAttemptsRef.current >= MAX_SCROLL_ATTEMPTS) {
      setPendingScrollGroupId(null);
    }
  }, [pendingScrollGroupId, viewMode, listingGroups]);

  useEffect(() => {
    emitViewPropertiesPageEvent({ user });
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, []);

  useEffect(() => {
    if (
      user?.features?.canAccessMainPayments &&
      !user?.hasSeenPaymentsAggregatedViewModal &&
      (user?.paymentsAcceptedCount ?? 0) >= 2
    ) {
      setShowPaymentsAggregateViewModal(true);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [user?.features?.canAccessMainPayments]);

  useEffect(() => {
    if (isCheckInitialDisplayModal.current && !isPending) {
      isCheckInitialDisplayModal.current = false;
      checkInitialDisplayModal();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [isPending, numTotalListings]);

  useEffect(() => {
    if (isError && propertiesError) {
      logError({
        error: propertiesError,
        context: 'fetchGroupableProperties',
      });
    }
  }, [isError, propertiesError]);

  useEffect(() => {
    const postingPath = router.query[QueryConstants.POSTING_PATH];

    if (postingPath) {
      checkInitialDisplayModal();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [router.query[QueryConstants.POSTING_PATH]]);

  const checkInitialDisplayModal = () => {
    renderInitialDisplayModal({
      router,
      totalListingsWithoutFilters: numTotalListings,
      showAddPropertyModal,
      setShowAddPropertyModal: handleAddPropertyClick,
      canAccessUserOnboardingNewUser,
      setShowingUserOnboardingExisitngUser,
      canAccessUserOnboardingExistingUser,
    });
  };

  useEffect(() => {
    if (!hasInitializedFromQuery.current) return;
    updateFlexibleRouterWithFilters(router, filters, viewMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, viewMode]);

  const updateFilters = (updater: (prev: FlexiblePropertiesFilterState) => FlexiblePropertiesFilterState) => {
    setFilters(updater);
  };

  const handleViewModeChange = (newViewMode: FlexiblePropertiesViewMode) => {
    setViewMode(newViewMode);
  };

  const handleClearFilters = () => {
    updateFilters((current) => ({ ...current, searchText: '', includeArchived: false }));
  };

  const handleShowArchived = () => {
    updateFilters((current) => ({ ...current, includeArchived: true }));
  };

  const handleAddPropertyClick = () => {
    if (user?.features?.canAddListing && user?.restrictions?.isAllowed) {
      setShowAddPropertyModal(true);
    }

    // TODO: add flexible properties analytics
    // emitOpenEvent({ user: user as ZrmClickstreamUser });
    // emitAddPropertyClickEvent({ user: user as ZrmClickstreamUser });
  };

  const canAccessFeatureNotifications = user ? accessUtils.canAccessFeatureNotifications({ user }) : false;

  const renderPropertiesView = () => {
    const sharedProps = {
      listingGroups: listingGroups ?? [],
      isLoading: isContentLoading || isAwaitingStreetEasyListings,
    };

    switch (viewMode) {
      case FLEXIBLE_PROPERTIES_VIEW_MODE.PROPERTIES:
        return <PropertiesTable {...sharedProps} hasNextPage={hasNextPage} />;

      case FLEXIBLE_PROPERTIES_VIEW_MODE.UNITS:
        return <UnitsListTable />;

      case FLEXIBLE_PROPERTIES_VIEW_MODE.BOTH:
      default:
        return <FlexiblePropertyCardList {...sharedProps} expansion={expansion} registerCard={registerCard} />;
    }
  };

  return (
    <AuthCheckWrapper loading={skeleton} redirecting={skeleton}>
      <Page.Root data-testid="FlexiblePropertiesPage" css={{ backgroundColor: 'bg.neutral' }}>
        <Head>
          <title>Properties | Zillow Rental Manager</title>
        </Head>

        {canAccessFeatureNotifications && <NotificationBanner />}

        {/* Page Heading */}
        <PageHeading
          buttonEmphasis="filled"
          buttonIconPosition="start"
          buttonIcon={<IconPlusFilled />}
          buttonSize="sm"
          handleAddPropertyClick={handleAddPropertyClick}
        />

        {/* Email verification and notifications */}
        <EmailVerification />
        <PropertiesPageNotifications />

        {/* Content / Tables */}
        <Page.Content css={{ padding: { base: '0 {spacing.600}', xl: '0 {spacing.1200}' } }}>
          {isLinkingStreetEasyUser && (
            <Alert tone="info" css={{ marginBottom: '600' }} data-testid="FlexiblePropertiesPage-StreetEasyLinking">
              Importing your StreetEasy listings. This should only take a moment.
            </Alert>
          )}

          {/* Has listings or initial loading */}
          {(hasAnyListings || isInitialLoading) && (
            <>
              {canAccessFeatureTour && (
                <FeatureTour
                  tourId={FT_FLEXIBLE_PROPERTIES_PAGE_CONFIG.tourId}
                  steps={FT_FLEXIBLE_PROPERTIES_PAGE_CONFIG.steps}
                />
              )}

              <Toolbar
                searchText={filters.searchText}
                onSearchTextChange={(searchText) => updateFilters((current) => ({ ...current, searchText }))}
                viewMode={viewMode}
                onViewModeChange={handleViewModeChange}
                isArchived={filters.includeArchived}
                onArchivedChange={(includeArchived) => updateFilters((current) => ({ ...current, includeArchived }))}
                isLoading={isContentLoading}
                isSearching={isContentLoading && Boolean(filters.searchText)}
              />

              <PortfolioFiltersProvider searchText={filters.searchText} includeArchived={filters.includeArchived}>
                {hasNoFilteredResults ? (
                  <FlexiblePropertyNoResultsEmptyState
                    searchText={filters.searchText}
                    includeArchived={filters.includeArchived}
                    onClearFilters={handleClearFilters}
                    onShowArchived={handleShowArchived}
                  />
                ) : (
                  renderPropertiesView()
                )}
              </PortfolioFiltersProvider>
            </>
          )}

          {/* Empty state */}
          {!hasAnyListings && !isContentLoading && !isLinkingStreetEasyUser && (
            <FlexiblePropertyEmptyState onAddPropertyClick={handleAddPropertyClick} />
          )}

          {/* The Units view paginates itself; this drives the property-based views. */}
          {viewMode !== FLEXIBLE_PROPERTIES_VIEW_MODE.UNITS && hasNextPage && (
            <Box css={{ display: 'flex', justifyContent: 'center', marginTop: '600' }}>
              <Button tone="brand" emphasis="filled" onClick={() => fetchNextPage()} loading={isFetchingNextPage}>
                Load more properties
              </Button>
            </Box>
          )}
        </Page.Content>

        {/* Modals */}
        {onboardingConfig && <UserOnboarding {...onboardingConfig} />}

        {showAddPropertyModal && (
          <AddPropertyFlowModal
            isOpen={showAddPropertyModal}
            onClose={() => {
              setShowAddPropertyModal(false);
              removeQueryParams(router, [QueryConstants.POSTING_PATH, QueryConstants.REDIRECT_TO]);
            }}
            onConfirm={(listingAlias) => {
              if (isRedirectToPayments(router.query)) {
                window.location.href = getPaymentsDashboardRoute({ listingAlias });
              }
            }}
            onAddTenant={setTenantModalListingAlias}
          />
        )}
        {showPaymentsAggregateViewModal && (
          <PaymentsAggregateViewModal
            onClose={() => setShowPaymentsAggregateViewModal(false)}
            paymentsAggregatePageUrl={EXTERNAL_ROUTES.PAYMENTS_DASHBOARD}
          />
        )}
        {tenantModalListingAlias && (
          <ModalTenantSelect
            client={client}
            listingAlias={tenantModalListingAlias}
            isOpen={true}
            queryClient={queryClient}
            onAddRenterSuccess={() => undefined}
            onClose={() => setTenantModalListingAlias(null)}
            onPrimaryCTAClick={() => setTenantModalListingAlias(null)}
          />
        )}
      </Page.Root>
    </AuthCheckWrapper>
  );
};

export default FlexiblePropertiesPage;
